import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Copy, Share2, Sparkles, Star, ArrowRight, Eye, Heart, Clock } from "lucide-react";
import { toast } from "sonner";
import { formatDownloads } from "@/lib/api";
import { SHARE } from "@/constants/testIds";

const THEMES = [
  { id: "neon", label: "Neon", bg: "#050505", accent: "#00F0FF", accent2: "#39FF14", grid: true },
  { id: "hot", label: "Hot", bg: "#0A0000", accent: "#FF003C", accent2: "#FFE300", grid: false },
  { id: "ice", label: "Ice", bg: "#02121A", accent: "#39FF14", accent2: "#00F0FF", grid: true },
  { id: "pop", label: "Pop", bg: "#0A0002", accent: "#FFE300", accent2: "#FF003C", grid: false },
];

export default function ShareCardModal({ open, onClose, app }) {
  const [theme, setTheme] = useState(THEMES[0]);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => { if (!open) setTheme(THEMES[0]); }, [open]);

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null, scale: 2, useCORS: true, logging: false,
      });
      const link = document.createElement("a");
      link.download = `jixxeo-${app.slug}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Card downloaded");
    } catch (e) {
      toast.error("Failed to render card");
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/app/${app.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  const nativeShare = async () => {
    const url = `${window.location.origin}/app/${app.slug}`;
    if (!navigator.share) { copyLink(); return; }
    setBusy(true);
    try {
      // If possible, share the rendered image
      if (cardRef.current) {
        const { default: html2canvas } = await import("html2canvas");
        const canvas = await html2canvas(cardRef.current, { backgroundColor: null, scale: 2, useCORS: true, logging: false });
        canvas.toBlob(async (blob) => {
          const file = new File([blob], `jixxeo-${app.slug}.png`, { type: "image/png" });
          try {
            await navigator.share({ title: app.name, text: `Check out ${app.name} on Jixxeo`, url, files: [file] });
          } catch {
            navigator.share({ title: app.name, url }).catch(() => {});
          }
          setBusy(false);
        }, "image/png");
        return;
      }
      await navigator.share({ title: app.name, url });
    } catch {
      // ignore
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/80 backdrop-blur-sm" onClick={onClose}>
          <motion.div
            data-testid={SHARE.modal}
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            className="glass-strong rounded-t-3xl sm:rounded-3xl w-full max-w-lg p-6 relative border border-[#00F0FF]/20 max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute right-4 top-4 text-white/50 hover:text-white z-10">
              <X className="w-5 h-5" />
            </button>
            <div className="mono text-[10px] uppercase tracking-widest text-[#00F0FF] mb-1">// share this find</div>
            <h3 className="font-heading font-black text-2xl uppercase tracking-tighter">Grab a Card</h3>
            <p className="text-white/60 text-sm mt-1">Save it as an image and drop it anywhere — Instagram, Discord, X, wherever.</p>

            {/* Card preview */}
            <div className="mt-5 flex justify-center">
              <div ref={cardRef} className="relative w-[320px] rounded-3xl overflow-hidden shadow-2xl"
                   style={{ background: theme.bg, aspectRatio: "9/16", boxShadow: `0 0 60px ${theme.accent}30` }}>
                {/* Background art */}
                {app.screenshots?.[0] && (
                  <>
                    <img src={app.screenshots[0]} crossOrigin="anonymous" alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
                    <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${theme.bg}00 0%, ${theme.bg}90 60%, ${theme.bg} 100%)` }} />
                  </>
                )}
                {theme.grid && (
                  <div className="absolute inset-0 opacity-25" style={{
                    backgroundImage: `linear-gradient(${theme.accent}22 1px, transparent 1px), linear-gradient(90deg, ${theme.accent}22 1px, transparent 1px)`,
                    backgroundSize: "24px 24px",
                  }} />
                )}
                <div className="relative h-full flex flex-col p-6">
                  {/* Header */}
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center font-heading font-black text-black" style={{ background: theme.accent }}>
                      J
                    </div>
                    <div className="font-heading font-black text-sm uppercase tracking-tighter text-white">
                      Jixxeo<span style={{ color: theme.accent }}>.</span>Apps
                    </div>
                    <div className="ml-auto text-[8px] mono uppercase tracking-widest text-white/50">// shared find</div>
                  </div>

                  {/* Middle */}
                  <div className="flex-1 flex flex-col justify-end">
                    <div className="flex items-center gap-3 mb-2">
                      {app.icon_url && (
                        <img src={app.icon_url} crossOrigin="anonymous" alt=""
                             className="w-16 h-16 rounded-2xl ring-2" style={{ borderColor: theme.accent }} />
                      )}
                      <div className="min-w-0">
                        <div className="text-[10px] mono uppercase tracking-widest text-white/60 uppercase-tight">{app.category}</div>
                        <div className="font-heading font-black text-2xl uppercase tracking-tighter leading-none text-white">
                          {app.name}
                        </div>
                      </div>
                    </div>
                    <div className="text-white/85 text-sm leading-snug">{app.tagline}</div>
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-3 mono text-[10px] text-white/70">
                      <span className="flex items-center gap-1" data-testid="share-card-rating">
                        <Star className="w-3 h-3" fill={theme.accent2} color={theme.accent2} /> {app.rating?.toFixed(1) || "—"}
                      </span>
                      <span className="flex items-center gap-1" data-testid="share-card-views">
                        <Eye className="w-3 h-3" /> {formatDownloads(app.views || 0)} views
                      </span>
                      <span className="flex items-center gap-1" data-testid="share-card-favorites">
                        <Heart className="w-3 h-3" /> {formatDownloads(app.favorites || 0)} favs
                      </span>
                      <span className="flex items-center gap-1" data-testid="share-card-play-time">
                        <Clock className="w-3 h-3" /> {app.avg_play_time_minutes || 18}m
                      </span>
                    </div>
                    {app.is_pwa && (
                      <div className="mt-3 inline-flex items-center gap-1 mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-full self-start"
                           style={{ background: `${theme.accent2}22`, color: theme.accent2, border: `1px solid ${theme.accent2}55` }}>
                        <Sparkles className="w-2.5 h-2.5" /> Instant PWA
                      </div>
                    )}
                  </div>

                  {/* Footer strip */}
                  <div className="mt-4 flex items-center justify-between border-t pt-3" style={{ borderColor: `${theme.accent}30` }}>
                    <div className="mono text-[9px] uppercase tracking-widest text-white/70">
                      jixxeo.apps/<span style={{ color: theme.accent }}>{app.slug}</span>
                    </div>
                    <ArrowRight className="w-4 h-4" style={{ color: theme.accent }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Theme picker */}
            <div className="flex gap-2 justify-center mt-5">
              {THEMES.map((t) => (
                <button key={t.id} onClick={() => setTheme(t)}
                  className="w-10 h-10 rounded-lg flex items-center justify-center border transition-transform hover:-translate-y-0.5"
                  style={{
                    background: t.bg,
                    borderColor: theme.id === t.id ? t.accent : "rgba(255,255,255,0.1)",
                    boxShadow: theme.id === t.id ? `0 0 12px ${t.accent}66` : "none",
                  }}>
                  <span className="w-4 h-4 rounded-full" style={{ background: t.accent }} />
                </button>
              ))}
            </div>

            {/* Actions */}
            <div className="grid grid-cols-3 gap-2 mt-6">
              <button data-testid={SHARE.download} onClick={download} disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 bg-[#00F0FF] hover:bg-white text-black font-bold mono text-[10px] uppercase tracking-widest py-3 rounded-full disabled:opacity-50">
                <Download className="w-3.5 h-3.5" /> {busy ? "…" : "PNG"}
              </button>
              <button onClick={nativeShare} disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 border border-[#39FF14]/40 bg-[#39FF14]/10 text-[#39FF14] font-bold mono text-[10px] uppercase tracking-widest py-3 rounded-full disabled:opacity-50">
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
              <button data-testid={SHARE.copy} onClick={copyLink}
                className="inline-flex items-center justify-center gap-1.5 border border-white/15 text-white/80 mono text-[10px] uppercase tracking-widest py-3 rounded-full hover:border-white/40">
                <Copy className="w-3.5 h-3.5" /> Link
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
